// Pure helpers shared by the insights chart plugins, overlay and menu.
import { INSIGHT_COLORS } from '../../utils/chartPalette';
import { CHART_TICK_PX } from '../chart/chartFonts';
import {
  TOOLTIP_LABELS, fmtMw, fmtPct, fmtSignedMw, observedValue, rampMethodOf,
} from '../../constants/insightsText';

export const SLOT_SEC = 15 * 60;
// The pin row above the plot, in CSS px: 18 px discs on 24 px buttons (WCAG
// 2.2 target size) whose bottom sits 5 px above the plot's top edge, clear of
// its top gridline. PIN_ROW_H is the room they take.
export const PIN = 18;
export const PIN_HIT = 24;
export const PIN_GAP = 5;
export const PIN_ROW_H = PIN_HIT + PIN_GAP;
// Rows under the plot, in CSS px from its bottom edge: the ramp ribbon
// ("Expected") and the Observed strip, 4 px apart. The ribbon starts 3 px
// below the lowest y tick label (centred on the plot's bottom edge), so the
// row captions clear it: 9 px with 12 px ticks.
export const RIBBON_ROW = { y: CHART_TICK_PX / 2 + 3, h: 8 };
export const OBSERVED_STRIP = { y: RIBBON_ROW.y + RIBBON_ROW.h + 4, h: 6 };
export const BARS_MAX_SEC = 3 * 24 * 3600;

// Start of the UTC day holding `sec`. "Past" means before today (UTC), so a
// pointer from earlier today stays in view.
export const utcDayStart = (sec) => Math.floor(sec / 86400) * 86400;

// Pointers the user wants to see: type filter, strength and time. Callers
// pass one `cutoffSec` per data load (insights slice `pastCutoff`) so pins,
// labels and counts agree; without it, the start of the current UTC day.
export function visiblePointers(pointers, { types = {}, strongOnly = true, showPast = false, cutoffSec } = {}) {
  const cutoff = cutoffSec ?? utcDayStart(Date.now() / 1000);
  return (pointers || []).filter((p) => {
    if (types[p.type] === false) return false;
    if (strongOnly && p.strength !== 'strong') return false;
    if (!showPast && Date.parse(p.end) / 1000 < cutoff) return false;
    return true;
  });
}

// Forecast-based ribbon level of a weak pointer's window: the plugin draws a
// level v at opacity v / 0.5, so strong windows are solid and weak ones light.
const WEAK_WINDOW = 0.15;

export const windowLevel = (p) => (p.strength === 'strong' ? 1 : WEAK_WINDOW);
const isRamp = (p) => p.type === 'ramp_down' || p.type === 'ramp_up';
// Insight rows were loaded but none holds a forecast-based ramp (About panel
// note for rows computed before the method). No rows (future days, never
// computed) is not that.
export const rowsWithoutForecastRamps = (data) => Boolean(data?.series?.datetime?.length)
  && !(data.pointers || []).some((p) => isRamp(p) && rampMethodOf(p, data.params) === 'forecast');

// Per slot, the strongest forecast-based `type` window covering it [start, end).
function windowLevels(t, pointers, type, strongOnly) {
  const out = t.map(() => 0);
  pointers.filter((p) => p.type === type && (!strongOnly || p.strength === 'strong')).forEach((p) => {
    const a = Date.parse(p.start) / 1000;
    const b = Date.parse(p.end) / 1000;
    const v = windowLevel(p);
    t.forEach((sec, i) => { if (sec >= a && sec < b && v > out[i]) out[i] = v; });
  });
  return out;
}

const secOf = (iso) => Date.parse(iso) / 1000;
const overlaps = (a, b) => secOf(a.start) < secOf(b.end) && secOf(a.end) > secOf(b.start);
// The tuning's hit rule: windows overlap (tolerance 0) and the direction is the same.
const sameRamp = (p, o) => p.type === o.type && overlaps(p, o);
// A gap ([first missing slot, next measured slot)) takes a window's end slot
// too: the change ends on that value (forecast_ramps.py, r(t0 + h)).
const missingIn = (p, g) => secOf(g.start) <= secOf(p.end) && secOf(g.end) > secOf(p.start);

// What the measurements say about a ramp pointer's window (addendum spec 7):
// a big ramp of its direction overlaps it ('hit'); else it is not fully
// measured yet ('unmeasured': it ends after the newest measured quarter-hour,
// `observed_until`, or after now when the payload lacks it) or measurements
// are missing in it ('gap': one of `observed_gaps` takes a slot of it, its
// end included); else 'miss'.
function measuredVerdict(p, observed, untilSec, gaps) {
  const event = observed.find((o) => sameRamp(p, o));
  if (event) return { verdict: 'hit', event };
  if (untilSec == null || secOf(p.end) > untilSec) return { verdict: 'unmeasured' };
  if (gaps.some((g) => missingIn(p, g))) return { verdict: 'gap' };
  return { verdict: 'miss' };
}

// Warnings: strong ramp pointers (of the method in force, the only ramps
// served) of the types shown; "Include past days" does not matter.
const warningsOf = (prep, types = {}) => (prep?.pointers || [])
  .filter((p) => isRamp(p) && p.strength === 'strong' && types[p.type] !== false);

// The warning of the same direction over a measured ramp, if any.
export const warningOver = (prep, o, types) => warningsOf(prep, types).find((p) => sameRamp(p, o));

// The Observed strip's marks: `covered` says whether a shown warning of the
// same direction overlaps it; null when that ramp indicator is hidden (no
// warning is checked).
export const observedMarks = (prep, types = {}) => (prep?.observed || []).map((o) => ({
  ...o, covered: types[o.type] === false ? null : Boolean(warningOver(prep, o, types)),
}));

// Measured ramps and how many a warning of the same direction overlaps; the menu's tally.
export function observedTally(prep, types = {}) {
  const observed = prep?.observed || [];
  const warnings = warningsOf(prep, types);
  return { observed: observed.length, warned: observed.filter((o) => warnings.some((p) => sameRamp(p, o))).length };
}

// Stretches of [min, max] (seconds) with measurements: up to the newest
// measured quarter-hour, without the gaps; the Observed strip's track.
export function measuredSpans(prep, min, max) {
  const until = prep.measuredUntil;
  if (until == null) return [];
  const end = Math.min(max, until);
  const spans = [];
  let at = min;
  prep.gaps.forEach(([a, b]) => {
    if (a > at && at < end) spans.push([at, Math.min(a, end)]);
    at = Math.max(at, b);
  });
  if (end > at) spans.push([at, end]);
  return spans;
}

// GET /market/insights payload -> arrays keyed by slot, times in seconds.
// Revisions come without insight rows, so pointers alone are enough (empty
// series, no ribbon); null when there is nothing at all to draw. With the
// forecast-based method the ribbon and bars show its windows, not the crowd share.
// `observed`: the measured ramps, null unless the payload has them (observed=1)
// under a rule and the layer is shown (`opts.observed`; turning it off hides
// the loaded ones without a request); ramp pointers then carry `measured`,
// the verdict for their side card.
export function prepareInsights(data, { observed: shown = true } = {}) {
  const s = data?.series || {};
  const datetime = Array.isArray(s.datetime) ? s.datetime : [];
  if (!data || (!datetime.length && !(data.pointers || []).length)) return null;
  const t = datetime.map((d) => Date.parse(d) / 1000);
  const params = data.params || {};
  const rampMethod = params.ramp_method || 'crowd';
  const forecastRamps = (data.pointers || []).filter((p) => rampMethodOf(p, params) === 'forecast');
  const forecast = rampMethod === 'forecast';
  // Without a rule in force (a use case without one) nothing was looked for.
  const observed = shown && Array.isArray(data.observed) && params.observed_rule ? data.observed : null;
  let untilSec = Date.now() / 1000;
  if ('observed_until' in data) untilSec = data.observed_until ? secOf(data.observed_until) : null;
  const gaps = data.observed_gaps || [];
  const pointers = (data.pointers || []).map((p) => (
    observed && isRamp(p) ? { ...p, measured: measuredVerdict(p, observed, untilSec, gaps) } : p));
  return {
    t,
    indexOf: new Map(t.map((sec, i) => [sec, i])),
    rampMethod,
    down: forecast ? windowLevels(t, forecastRamps, 'ramp_down', false) : s.ramp_down_share || [],
    up: forecast ? windowLevels(t, forecastRamps, 'ramp_up', false) : s.ramp_up_share || [],
    // "Strong pointers only" hides weak forecast-based windows (see rampLevels).
    strongLevels: forecast
      ? { down: windowLevels(t, forecastRamps, 'ramp_down', true), up: windowLevels(t, forecastRamps, 'ramp_up', true) }
      : null,
    rateP10: s.rate_p10 || [],
    rateP50: s.rate_p50 || [],
    rateP90: s.rate_p90 || [],
    crowdQ: s.crowd_q || [],
    n: s.n || [],
    split: s.split || [],
    pointers,
    observed,
    measuredUntil: untilSec,
    gaps: gaps.map((g) => [secOf(g.start), secOf(g.end)]).sort((a, b) => a[0] - b[0]),
    params,
  };
}

// Ramp levels drawn at slot `i` for the plugin state `st`: the types filter,
// and for forecast-based windows "Strong pointers only", so the strip matches
// the pins. Crowd shares are drawn whatever the strength filter.
export function rampLevels(st, i) {
  const src = st.prep.strongLevels && st.strongOnly !== false ? st.prep.strongLevels : st.prep;
  return {
    down: st.types?.ramp_down === false ? 0 : src.down[i] || 0,
    up: st.types?.ramp_up === false ? 0 : src.up[i] || 0,
  };
}

export const barsVisible = (min, max) => max - min < BARS_MAX_SEC;

// Colour for a kind of mark; ``text`` picks the text-safe tone for labels.
export function insightTone(kind, dark, text = false) {
  const base = kind === 'rise' || kind === 'ramp_up' ? 'rise' : kind === 'weak' ? 'weak' : 'drop';
  const key = text && base !== 'weak' ? `${base}Text` : base;
  return dark ? INSIGHT_COLORS[key].dark : INSIGHT_COLORS[key].light;
}

// Greedy rows: each label (sorted by x) takes the first row where it does
// not come within ``gap`` px of a label already placed.
export function placeLabels(items, gap = 8) {
  const placed = [];
  const taken = (row, x0, x1) => placed.some((p) => p.row === row && x0 < p.x1 + gap && x1 > p.x0 - gap);
  return items.slice().sort((a, b) => a.x - b.x).map((it) => {
    const x0 = it.x - it.w / 2;
    const x1 = it.x + it.w / 2;
    let row = 0;
    while (taken(row, x0, x1)) row += 1;
    const out = { ...it, row, x0, x1 };
    placed.push(out);
    return out;
  });
}

// Labels that start at their own ``y`` (above a bar) and move up by ``step``
// px while they would touch a label already placed (prototype R8 peaks).
export function stackLabels(items, gap = 8, step = 15) {
  const placed = [];
  const taken = (y, x0, x1) => placed.some((p) => x0 < p.x1 + gap && x1 > p.x0 - gap && Math.abs(y - p.y) < step);
  return items.slice().sort((a, b) => a.x - b.x).map((it) => {
    const x0 = it.x - it.w / 2;
    const x1 = it.x + it.w / 2;
    let { y } = it;
    while (taken(y, x0, x1)) y -= step;
    const out = { ...it, x0, x1, y };
    placed.push(out);
    return out;
  });
}

// Hover rows: the crowd at the slot under the cursor, then any observed ramp
// covering it (those need no insight row).
export function insightsTooltipRows(prep, sec, types = {}) {
  if (!prep) return [];
  const i = prep.indexOf.get(sec);
  const observed = types.observed === false ? [] : (prep.observed || [])
    .filter((o) => sec >= secOf(o.start) && sec < secOf(o.end))
    .map((o) => ({ kind: o.type === 'ramp_up' ? 'rise' : 'drop', label: TOOLTIP_LABELS.observed(o), value: observedValue(o) }));
  return (i == null ? [] : slotRows(prep, i, types)).concat(observed);
}

function slotRows(prep, i, types) {
  const rows = [];
  const q = prep.crowdQ[i];
  if (types.crowd !== false && q) {
    rows.push({ kind: 'crowd', label: TOOLTIP_LABELS.crowd(prep.n[i]), value: `${fmtMw(q[2])}–${fmtMw(q[18])} MW` });
  }
  // The crowd shares only with the crowd method: forecast-based levels are windows.
  const shares = prep.rampMethod !== 'forecast';
  if (shares && types.ramp_down !== false && prep.down[i] >= 0.02) {
    rows.push({ kind: 'drop', label: TOOLTIP_LABELS.drop, value: `${fmtPct(prep.down[i])} of forecasters` });
  }
  if (shares && types.ramp_up !== false && prep.up[i] >= 0.02) {
    rows.push({ kind: 'rise', label: TOOLTIP_LABELS.rise, value: `${fmtPct(prep.up[i])} of forecasters` });
  }
  const r = prep.rateP50[i];
  if (r != null && Math.abs(r) >= 50) {
    rows.push({
      kind: r < 0 ? 'drop' : 'rise',
      label: TOOLTIP_LABELS.rate,
      value: `${fmtSignedMw(r)} MW/h (${fmtSignedMw(prep.rateP10[i])} to ${fmtSignedMw(prep.rateP90[i])})`,
    });
  }
  const sp = prep.split[i];
  if (types.split !== false && sp) {
    rows.push({
      kind: 'split',
      label: TOOLTIP_LABELS.split,
      value: `${fmtPct(sp.upper_share)} ≈${fmtMw(sp.upper)} MW / ${fmtPct(1 - sp.upper_share)} ≈${fmtMw(sp.lower)} MW`,
    });
  }
  return rows;
}
